import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Stack, useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import { MembershipAPI } from "../../../api/membership";
import VisibilityIcon from "../../../assets/SVGs/Login/VisibilityIcon";
import EditClosedBusinessModal from "../../Modal/EditClosedBusinessModal";
import { defaultEditFormState } from "../../constant";
import { formatCurrency } from "../../helper";
import MemberNameCell from "../../MemberNameCell";

const ClosedBusinessBothTab = ({ id, timeframe }) => {
  const navigate = useNavigate();
  const isMobileTable = useMediaQuery("(max-width:850px)");
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openEditModal, setOpenEditModal] = useState(defaultEditFormState);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const referredData = await MembershipAPI.getClosedBusiness(id, "sent");
      const receivedData = await MembershipAPI.getClosedBusiness(
        id,
        "received",
      );

      const startDate = dayjs(timeframe);
      const endDate = dayjs();

      const filteredReferredData = referredData?.items.filter((item) => {
        const closedDate = dayjs(item.closed_business_date);
        return (
          closedDate.isValid() &&
          closedDate.isBetween(startDate, endDate, null, "[]")
        );
      });

      const filteredReceivedData = receivedData?.items.filter((item) => {
        const closedDate = dayjs(item.closed_business_date);
        return (
          closedDate.isValid() &&
          closedDate.isBetween(startDate, endDate, null, "[]")
        );
      });

      const combinedData = [
        ...new Map(
          [
            ...(filteredReferredData || []),
            ...(filteredReceivedData || []),
          ].map((item) => [item.view_id, item]),
        ).values(),
      ];

      setData(combinedData);
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, timeframe, navigate]);

  const columns = [
    {
      field: "closed_business_date",
      headerName: "Closed Business Date",
      flex: 1,
      minWidth: isMobileTable ? 150 : undefined,
      type: "date",
      valueGetter: (params) => {
        return dayjs(params).toDate(); // returns a native JS Date
      },
      renderCell: (params) => {
        return params.value ? dayjs(params.value).format("MM/DD/YYYY") : "";
      },
    },
    {
      field: "closed_business_sender",
      headerName: "Sender",
      flex: 1,
      minWidth: isMobileTable ? 150 : undefined,
      valueGetter: (value, row) => {
        const sender = row?.closed_business_sender;
        return sender
          ? `${sender.first_name || ""} ${sender.last_name || ""}`.trim()
          : "";
      },
      renderCell: (params) => (
        <MemberNameCell member={params?.row?.closed_business_sender} />
      ),
    },
    {
      field: "closed_business_receiver",
      headerName: "Receiver",
      flex: 1,
      minWidth: isMobileTable ? 150 : undefined,
      valueGetter: (value, row) => {
        const receiver = row?.closed_business_receiver;
        return receiver
          ? `${receiver.first_name || ""} ${receiver.last_name || ""}`.trim()
          : "";
      },
      renderCell: (params) => (
        <MemberNameCell member={params?.row?.closed_business_receiver} />
      ),
    },
    {
      field: "dollar_amount_of_realized_revenue",
      headerName: "Realized Revenue",
      flex: 1,
      minWidth: isMobileTable ? 150 : undefined,
      renderCell: (params) => {
        return formatCurrency(params.value);
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="view"
          size="small"
          onClick={() =>
            setOpenEditModal({
              open: true,
              id: params?.row?.id,
              title: params?.row?.closed_business_name,
              invitationType: params?.row?.invitation_type,
            })
          }
        >
          <VisibilityIcon
            sx={{
              color: "#64748B",
            }}
          />
        </IconButton>
      ),
    },
  ];

  return (
    <Stack
      sx={{
        width: "100%",
        "& .MuiDataGrid-root": {
          borderRadius: "8px",
        },
      }}
      className="customDataGridFigma"
    >
      <DataGrid
        rows={data || []}
        columns={columns}
        getRowId={(row) => row.view_id}
        loading={isLoading}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        checkboxSelection={false}
        rowSelection={false}
        rowHeight={48}
        columnHeaderHeight={40}
        getRowClassName={(params) => {
          const customClassName =
            params.indexRelativeToCurrentPage % 2 !== 0 ? "bg-gray" : "";
          return ` ${customClassName}`.trim();
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "linear-progress",
          },
        }}
        localeText={{
          noRowsLabel: "No Closed Businesses found.",
        }}
        disableVirtualization
      />
      {openEditModal?.open && (
        <EditClosedBusinessModal
          open={openEditModal?.open}
          id={openEditModal?.id}
          handleClose={() => setOpenEditModal(defaultEditFormState)}
          setIsLoading={setIsLoading}
          title={openEditModal?.title}
          invitationType={openEditModal?.invitationType}
        />
      )}
    </Stack>
  );
};

export default ClosedBusinessBothTab;
